import {
  Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel,
  Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Tab, Tabs,
  IconButton, Typography, Paper, Tooltip, Chip
} from "@mui/material";
import moment from "moment";
import { useEffect, useState, useMemo } from "react";
import DatePicker from "react-datepicker";
import { Tower } from "../../types/towerTypes";
import { ProjectActivity } from "../../types/ActivityTypes";
import { addDays, format, parse, startOfWeek, endOfWeek } from "date-fns";
import "moment/locale/es";
import { getProjectActivities } from "../../api/activityApi";
import { getTowersByProjectId } from "../../api/towerApi";
import Save from "@mui/icons-material/Save";
import Cancel from "@mui/icons-material/Cancel";
import Edit from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  createWeeklyProgramming,
  deleteWeeklyProgramming,
  exportWeeklyProgramming,
  getTowerByIdAndBetwenData,
  getAllByProject // Importante: Asegúrate de tener esta función en tu API para el historial
} from "../../api/weeklyProgrammingApi";
import { getEmployeesByIdProject } from "../../api/employeeApi";
import { Project } from "../../types/projectTypes";
import { getUserProject } from "../../api/userApi";
import { getActiveProjects } from "../../api/projectApi";
import { profiles, ADMIN_SISTEMA } from "../../contexts/constants/profiles";
import { Snackbar, Alert } from "@mui/material";

moment.locale("es");

const naturalSort = (a: string | undefined, b: string | undefined) => {
  const strA = a || "";
  const strB = b || "";
  return strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' });
};

const WeeklyProgramming: React.FC = () => {
  // --- Estados Generales ---
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectInput, setProjectInput] = useState("");
  const [selectedProject, setSelectedProject] = useState<number>();
  const [tabValue, setTabValue] = useState(0); // 0: Editor, 1: Historial

  // --- Estados de Programación (Editor) ---
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [highlightedDates, setHighlightedDates] = useState<Date[]>([]);

  const [towers, setTowers] = useState<Tower[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<string>("");
  const [schedule, setSchedule] = useState<any[]>([]);
  const [error, setError] = useState<string>("");
  const [supervisors, setSupervisors] = useState<any[]>([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState<any | null>(null);
  const [observation, setObservation] = useState<string>("");
  const [team, setTeam] = useState<string>("");
  const [role, setRole] = useState<string>("");

  const [selectedStage, setSelectedStage] = useState<string>("");
  const [activitiesProject, setActivitiesProject] = useState<ProjectActivity[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<ProjectActivity[]>([]);
  const [uniqueActivities, setUniqueActivities] = useState<string[]>([]);
  const [filteredSubActivities, setFilteredSubActivities] = useState<string[]>([]);
  const [selectedSubActivity, setSelectedSubActivity] = useState<string>("");

  const [editableRow, setEditableRow] = useState<number | null>(null);
  const [tempData, setTempData] = useState<any[]>([]);
  const [previousSchedule, setPreviousSchedule] = useState<any[]>([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [isChecked, setIsChecked] = useState(false);

  // --- Estados Historial (Nueva Pestaña) ---
  const [historyData, setHistoryData] = useState<any[]>([]);

  const uniqueSections = useMemo(() => Array.from(
    new Set(activitiesProject.map((activity) => activity.activity.section))
  ), [activitiesProject]);

  // --- Manejo de Tabs ---
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1 && selectedProject) {
      loadHistory(selectedProject);
    }
  };

  // --- Lógica del Historial (Visualización de Informes) ---
  const loadHistory = async (idProject: number) => {
    try {
      // Obtenemos todos los registros para armar el historial
      const allRecords = await getAllByProject(idProject);

      const groupedByWeek: Record<string, any> = {};

      allRecords.forEach(record => {
        // CORRECCIÓN TS 1: Casteo explícito a string antes de new Date
        const dateString = record.periodDate as unknown as string;
        const date = new Date(dateString);

        // Agrupar por el Lunes de esa semana
        const monday = startOfWeek(date, { weekStartsOn: 1 });
        const key = format(monday, "yyyy-MM-dd");

        if (!groupedByWeek[key]) {
          groupedByWeek[key] = {
            startDate: monday,
            endDate: endOfWeek(date, { weekStartsOn: 1 }),
            recordsCount: 0,
            supervisors: new Set(),
            lastUpdate: date
          };
        }

        groupedByWeek[key].recordsCount += 1;
        if (record.supervisor) groupedByWeek[key].supervisors.add(record.supervisor);
        if (date > groupedByWeek[key].lastUpdate) groupedByWeek[key].lastUpdate = date;
      });

      // Convertir a array y ordenar
      const historyList = Object.keys(groupedByWeek).map(key => ({
        startDate: groupedByWeek[key].startDate,
        endDate: groupedByWeek[key].endDate,
        count: groupedByWeek[key].recordsCount,
        supervisors: Array.from(groupedByWeek[key].supervisors).join(", "),
        lastUpdate: groupedByWeek[key].lastUpdate
      })).sort((a, b) => b.startDate.getTime() - a.startDate.getTime());

      setHistoryData(historyList);

    } catch (error) {
      console.error("Error cargando historial", error);
    }
  };

  const handleViewReport = (startDate: Date) => {
    setSelectedDate(startDate); // Setea la fecha
    setTabValue(0); // Vuelve a la pestaña de edición
  };

  const handleDownloadReport = async (startDate: Date, endDate: Date) => {
      if (!selectedProject) return;
      const startStr = format(startDate, "yyyy-MM-dd'T'00:00:00");
      const endStr = format(endDate, "yyyy-MM-dd'T'23:59:59");

      try {
        await exportWeeklyProgramming(selectedProject, startStr, endStr);
      } catch (e) {
        console.error(e);
        alert("Error al descargar el informe.");
      }
  };

  // --- Checkbox Semana Anterior ---
  const handleCheckboxChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsChecked(checked);

    if (checked) {
      try {
        const previousWeekData = await getPreviousWeekSchedule(highlightedDates);
        setSchedule(prevSchedule => [...prevSchedule, ...previousWeekData]);
      } catch (error) {
        console.error("Error al cargar datos de la semana anterior:", error);
      }
    } else {
      setSchedule([]);
    }
  };

  const getPreviousWeekSchedule = async (currentHighlightedDates: Date[]) => {
    const previousWeekDates = currentHighlightedDates.map(date =>
      new Date(date.getTime() - 7 * 24 * 60 * 60 * 1000)
    );

    if (selectedProject) {
      const startDate = format(
        startOfWeek(previousWeekDates[0], { weekStartsOn: 1 }),
        "yyyy-MM-dd'T'00:00:00"
      );
      const endDate = format(
        addDays(startOfWeek(previousWeekDates[0], { weekStartsOn: 1 }), 5),
        "yyyy-MM-dd'T'23:59:59"
      );

      try {
        const response = await getTowerByIdAndBetwenData(selectedProject, startDate, endDate);
        const groupedData = response.reduce((acc: any, current: any) => {
          const activityId = current.activity.id;
          const workFront = current.workFront;

          let activityEntry = acc.find(
            (entry: any) => entry.activity.id === activityId && entry.workFront === workFront
          );

          if (!activityEntry) {
            activityEntry = {
              activity: current.activity,
              supervisor: current.supervisor,
              role: current.position,
              team: current.equipament,
              observations: current.coment,
              workFront: current.workFront,
              towers: []
            };
            acc.push(activityEntry);
          }

          const dayIndex = previousWeekDates.findIndex(date =>
            format(date, "dd/MM/yyyy") === format(new Date(current.periodDate), "dd/MM/yyyy")
          );

          if (dayIndex !== -1) {
            const newDay = format(addDays(previousWeekDates[dayIndex], 7), "dd/MM/yyyy");
            activityEntry.towers.push({
              id: null,
              towerNumber: current.towerName,
              day: newDay
            });
          }
          return acc;
        }, []);
        return groupedData;
      } catch (error) {
        console.error("Error al cargar datos de la semana anterior:", error);
        return [];
      }
    } else {
      return [];
    }
  };

  // --- Effects ---
  useEffect(() => {
    const fetchProjects = async () => {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const parsedUser = JSON.parse(userStr);
      const rut = parsedUser.identificationNum;
      const role = parsedUser.rols?.description || "";

      let data: Project[] = [];
      if (role === profiles[0].name || role === profiles[2].name || role === ADMIN_SISTEMA) {
        data = await getActiveProjects();
      } else {
        const userProjects = await getUserProject(rut);
        data = Array.isArray(userProjects) ? userProjects : [];
      }

      setProjects(data.filter(p => p && (p.status === undefined || p.status === true)));
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (selectedProject) {
        const activitiesData = await getProjectActivities(selectedProject);
        let filtered = activitiesData;

        filtered.sort((a: any, b: any) => {
             const orderDiff = (a.order || 9999) - (b.order || 9999);
             if (orderDiff !== 0) return orderDiff;
             const sectionDiff = (a.activity.section || '').localeCompare(b.activity.section || '');
             if (sectionDiff !== 0) return sectionDiff;
             const nameDiff = (a.activity.name || '').localeCompare(b.activity.name || '');
             if (nameDiff !== 0) return nameDiff;
             return naturalSort(a.activity.subActivity, b.activity.subActivity);
        });

        setActivitiesProject(filtered);

        const fetchedTowers = await getTowersByProjectId(selectedProject);
        setTowers(fetchedTowers);

        const normalizeText = (text: string) =>
          text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

        const employees = await getEmployeesByIdProject(selectedProject);
        const filteredSupervisors = employees.filter((employee: any) => {
          const positionNormalized = normalizeText(employee.position || "");
          return ["supervisor", "capataz", "topografo"].some(keyword =>
            positionNormalized.includes(normalizeText(keyword))
          );
        });

        setSupervisors(filteredSupervisors);

        // Si estamos en la pestaña historial, recargar
        if (tabValue === 1) {
             loadHistory(selectedProject);
        }
      }
    };
    fetchProjectData();
  }, [selectedProject]);

  useEffect(() => {
    const fetchProjectSchedule = async () => {
      if (!selectedProject || !selectedDate) return;

      const startDate = format(
        startOfWeek(selectedDate, { weekStartsOn: 1 }),
        "yyyy-MM-dd'T'00:00:00"
      );
      const endDate = format(
        addDays(startOfWeek(selectedDate, { weekStartsOn: 1 }), 5),
        "yyyy-MM-dd'T'23:59:59"
      );

      try {
        const response = await getTowerByIdAndBetwenData(selectedProject, startDate, endDate);
        const newHighlightedDates = Array.from({ length: 6 }, (_, i) =>
          addDays(startOfWeek(selectedDate, { weekStartsOn: 1 }), i)
        );
        setHighlightedDates(newHighlightedDates);

        const formattedSchedule = response.reduce((acc: any[], current: any) => {
          const day = format(new Date(current.periodDate), "dd/MM/yyyy");

          let existingEntry = acc.find(
            (entry) => entry.activity.id === current.activity.id && entry.workFront === current.workFront
          );

          if (!existingEntry) {
            existingEntry = {
              activity: current.activity,
              supervisor: current.supervisor
                ? {
                  name: current.supervisor,
                  rut: current.supervisorRut || null,
                  position: current.position || "",
                }
                : null,
              role: current.position || "",
              team: current.equipament,
              observations: current.coment,
              workFront: current.workFront,
              towers: []
            };
            acc.push(existingEntry);
          }

          existingEntry.towers.push({
            id: current.id,
            towerNumber: current.towerName,
            day
          });

          return acc;
        }, []);

        setSchedule(formattedSchedule);
        setTempData(formattedSchedule);
        setPreviousSchedule(formattedSchedule);
      } catch (error) {
        console.error("Error al cargar la programación del proyecto:", error);
        setSchedule([]);
      }
    };

    fetchProjectSchedule();
  }, [selectedProject, selectedDate]);


  // --- Handlers UI ---

  const handleDateChange = async (date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      const weekDates = Array.from({ length: 6 }, (_, i) =>
        addDays(startOfWeek(date, { weekStartsOn: 1 }), i)
      );
      setHighlightedDates(weekDates);
      // La recarga de datos se hace en el useEffect dependiente de selectedDate
    }
  };

  const handleAddSchedule = () => {
    if (!selectedProject) { setError("Debe seleccionar un proyecto."); return; }
    if (!selectedDate) { setError("Debe seleccionar un periodo."); return; }
    if (!selectedActivity || (!selectedSupervisor?.name && !role)) { setError("Debe seleccionar actividad y supervisor."); return; }

    const selectedActivityObject = activitiesProject.find(
      (activity) => activity.activity.name === selectedActivity && activity.activity.subActivity === selectedSubActivity
    );

    if (!selectedActivityObject) { setError("No se encontró la actividad."); return; }

    const existingWorkFronts = schedule
      .filter(entry => entry.activity.id === selectedActivityObject.activity.id)
      .map(entry => entry.workFront);

    const nextWorkFront = existingWorkFronts.length > 0 ? Math.max(...existingWorkFronts) + 1 : 1;

    const newScheduleEntry = {
      activity: selectedActivityObject.activity,
      supervisor: selectedSupervisor
        ? typeof selectedSupervisor === "string"
          ? { name: selectedSupervisor, position: role || "" }
          : selectedSupervisor
        : null,
      role: role || (selectedSupervisor?.position || ""),
      team,
      observations: observation,
      day: selectedDate ? format(selectedDate, "dd/MM/yyyy") : "",
      towers: [],
      workFront: nextWorkFront
    };

    setSchedule((prev) => [...prev, newScheduleEntry]);
    setTempData((prev) => [...prev, newScheduleEntry]);

    // Opcional: Limpiar campos
    // setSelectedActivity("");
    setRole("");
    setTeam("");
    setObservation("");
    setError("");
  };

  const handleSaveSchedule = async () => {
    if (!selectedProject) { alert("El ID del proyecto es necesario."); return; }

    try {
      const towersToAdd = getTowersToAdd(schedule);
      const towersToDelete = getTowersToDelete(schedule, previousSchedule);
      const allTowers = extractAllTowers(schedule);

      await deleteTowers(towersToDelete);
      await saveTowers(towersToAdd);
      await saveExistingTowers(allTowers);

      setPreviousSchedule(schedule);
      setSnackbarMessage("¡Programación guardada exitosamente!");
      setSnackbarOpen(true);
      setIsChecked(false);

      // Si estamos guardando, recargamos el historial en caso de que el usuario cambie de pestaña
      if(selectedProject) loadHistory(selectedProject);

    } catch (error) {
      console.error("❌ Error al guardar:", error);
      alert("Ocurrió un error al guardar.");
    }
  };

  // --- Helpers de Guardado ---
  const getTowersToAdd = (schedule: any[]) => {
    return schedule.flatMap((entry) => {
      const { activity, towers, supervisor, team, role, observations, workFront } = entry;
      const previousEntry = previousSchedule.find(
        (prev) => prev.activity.id === activity.id && prev.workFront === workFront
      );
      const previousTowers = previousEntry ? previousEntry.towers : [];

      return towers
        .filter((tower: any) =>
          !previousTowers.some(
            (prevTower: any) =>
              prevTower.towerNumber === tower.towerNumber &&
              prevTower.day === tower.day
          )
        )
        .map((tower: any) => ({
          id: tower.id || null,
          activity,
          periodDate: format(parse(tower.day, "dd/MM/yyyy", new Date()), "yyyy-MM-dd'T'00:00:00"),
          idProject: selectedProject,
          towerName: tower.towerNumber,
          supervisor: supervisor?.name || "",
          position: role,
          equipament: team,
          coment: observations,
          workFront,
        }));
    });
  };

  const getTowersToDelete = (schedule: any[], previousSchedule: any[]) => {
    return previousSchedule.flatMap((prevEntry) => {
      const currentEntry = schedule.find(
        (entry) => entry.activity.id === prevEntry.activity.id && entry.workFront === prevEntry.workFront
      );

      if (!currentEntry) return prevEntry.towers.map((tower: any) => tower.id);

      return prevEntry.towers
        .filter((prevTower: any) =>
          !currentEntry.towers.some(
            (tower: any) =>
              tower.towerNumber === prevTower.towerNumber &&
              tower.day === prevTower.day
          )
        )
        .map((tower: any) => tower.id);
    });
  };

  const extractAllTowers = (schedule: any[]) => {
    return schedule.flatMap((entry) =>
      entry.towers.map((tower: any) => ({
        id: tower.id || null,
        activity: entry.activity,
        periodDate: format(parse(tower.day, "dd/MM/yyyy", new Date()), "yyyy-MM-dd'T'00:00:00"),
        idProject: selectedProject,
        towerName: tower.towerNumber,
        supervisor: entry.supervisor?.name || "",
        position: entry.role,
        equipament: entry.team,
        coment: entry.observations,
        workFront: entry.workFront,
      }))
    );
  };

  const deleteTowers = async (towersToDelete: number[]) => {
    for (const towerId of towersToDelete) {
      if (towerId) await deleteWeeklyProgramming(towerId);
    }
  };

  const saveTowers = async (towersToAdd: any[]) => {
    for (const tower of towersToAdd) {
      if (tower.id === null || tower.id === undefined) await createWeeklyProgramming(tower);
    }
  };

  const saveExistingTowers = async (allTowers: any[]) => {
    for (const tower of allTowers) {
      if (tower.id) await createWeeklyProgramming(tower);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleStageChange = (e: SelectChangeEvent<string>) => {
    const stage = e.target.value;
    setSelectedStage(stage);

    const activitiesForStage = activitiesProject.filter(
      (activity) => activity.activity.section === stage
    );
    const uniqueActs = Array.from(
      new Set(activitiesForStage.map((activity) => activity.activity.name))
    ).filter(Boolean) as string[];

    setUniqueActivities(uniqueActs);
    setFilteredActivities(activitiesForStage);
  };

  const handleSelectActivityChange = (e: SelectChangeEvent<string>) => {
    const selectedActivityName = e.target.value;
    setSelectedActivity(selectedActivityName);
    setSelectedSubActivity("");

    const matchingActivities = activitiesProject.filter(
      (activity) => activity.activity.name === selectedActivityName
    );

    if (matchingActivities.length > 0) {
      const uniqueSubActivities = Array.from(
        new Set(matchingActivities.map((activity) => activity.activity.subActivity).filter(Boolean))
      ) as string[];

      setFilteredSubActivities(uniqueSubActivities);

      if (uniqueSubActivities.length === 1) {
        setSelectedSubActivity(uniqueSubActivities[0]);
        const selectedActivityObject = matchingActivities.find(
          (activity) => activity.activity.subActivity === uniqueSubActivities[0]
        );
        if (selectedActivityObject) {
          setSelectedActivity(selectedActivityObject.activity.name || "");
        }
      }
    } else {
      setFilteredSubActivities([]);
    }
  };

  const handleSelectSubActivityChange = (e: SelectChangeEvent<string>) => {
    const selectedSubActivityName = e.target.value;
    setSelectedSubActivity(selectedSubActivityName);

    const selectedActivityObject = activitiesProject.find(
      (activity) => activity.activity.name === selectedActivity && activity.activity.subActivity === selectedSubActivityName
    );

    if (selectedActivityObject) {
      setSelectedActivity(selectedActivityObject.activity.name || "");
    }
  };

  const handleDeleteActivity = async (activityId: number, workFront: number) => {
    try {
      const entryToDelete = schedule.find(
        (entry) => entry.activity.id === activityId && entry.workFront === workFront
      );
      if (!entryToDelete) return;

      const towerIdsToDelete = entryToDelete.towers
        .filter((tower: any) => tower.id)
        .map((tower: any) => tower.id);

      for (const towerId of towerIdsToDelete) await deleteWeeklyProgramming(towerId);

      setSchedule((prev) => prev.filter((entry) => !(entry.activity.id === activityId && entry.workFront === workFront)));
      setPreviousSchedule((prev) => prev.filter((entry) => !(entry.activity.id === activityId && entry.workFront === workFront)));

      setSnackbarMessage("¡Fila borrada exitosamente!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error al eliminar:", error);
      alert("Ocurrió un error al eliminar.");
    }
  };

  // CORRECCIÓN TS 2: Firma correcta para Autocomplete
  const handleAutocompleteChange = (
    _: React.SyntheticEvent,
    newValue: string | null | undefined
  ) => {
    const projectFind = projects.find((project) => project.name === newValue);
    setSelectedProject(projectFind?.id);

    // Resetear estados
    setSelectedStage("");
    setSelectedActivity("");
    setSelectedSubActivity("");
    setFilteredActivities([]);
    setFilteredSubActivities([]);
    setUniqueActivities([]);
    setSelectedSupervisor(null);
    setRole("");
    setTeam("");
    setObservation("");
    setIsChecked(false);
    setError("");
    setSchedule([]);
    setHistoryData([]); // Limpiar historial
  };

  const handleExportToExcel = async () => {
    if (!selectedProject || !selectedDate) {
      alert("Debes seleccionar un proyecto y una fecha.");
      return;
    }
    const startDate = format(startOfWeek(selectedDate, { weekStartsOn: 1 }), "yyyy-MM-dd'T'00:00:00");
    const endDate = format(addDays(startOfWeek(selectedDate, { weekStartsOn: 1 }), 5), "yyyy-MM-dd'T'23:59:59");

    try {
      await exportWeeklyProgramming(selectedProject, startDate, endDate);
    } catch {
      alert("Error al exportar el archivo.");
    }
  };

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Box sx={{ p: 3 }}>
        {/* SECTION: Header con Selector de Proyecto y Fecha */}
        <Box sx={{ p: 2, backgroundColor: "#f9f9f9", borderRadius: 2, border: "1px solid #ddd", mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={8} md={6}>
              <Autocomplete
                options={projects.map((project) => project.name)}
                freeSolo
                inputValue={projectInput}
                onInputChange={(event, newInputValue) => { setProjectInput(newInputValue); }}
                onChange={handleAutocompleteChange}
                renderInput={(params) => (
                  <TextField {...params} label="Seleccionar o buscar proyecto" variant="outlined" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Solo mostramos el DatePicker si estamos en modo Edición (Tab 0) */}
              {tabValue === 0 && (
                  <DatePicker
                    selected={selectedDate}
                    onChange={handleDateChange}
                    highlightDates={[{ "react-datepicker__day--highlighted": highlightedDates }]}
                    locale="es"
                    dateFormat="dd/MM/yyyy"
                    calendarStartDay={1}
                    popperClassName="custom-datepicker-popper"
                    customInput={
                      <TextField
                        label="Selecciona el día del periodo"
                        variant="outlined"
                        fullWidth
                        value={selectedDate ? moment(selectedDate).format("DD/MM/YYYY") : ""}
                      />
                    }
                  />
              )}
            </Grid>
          </Grid>
        </Box>

        {/* SECTION: Pestañas */}
        <Paper sx={{ mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="fullWidth">
                <Tab label="Programación Semanal (Editor)" />
                <Tab label="Historial de Informes" />
            </Tabs>
        </Paper>

        {/* --- PESTAÑA 0: EDITOR --- */}
        {tabValue === 0 && (
            <Box>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    borderRadius: 2,
                    border: "1px solid #ddd",
                  }}
                >
                  <Grid container spacing={2} marginTop={2}>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <InputLabel>Etapa</InputLabel>
                        <Select
                          label="Etapa"
                          name="stage"
                          value={selectedStage}
                          onChange={handleStageChange}
                        >
                          {uniqueSections.map((section, index) => (
                            <MenuItem key={index} value={section}>
                              {section}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <InputLabel>Actividad</InputLabel>
                        <Select
                          label="Actividad"
                          name="activity"
                          value={selectedActivity}
                          onChange={handleSelectActivityChange}
                        >
                          {uniqueActivities.map((activityName, index) => (
                            <MenuItem key={index} value={activityName}>
                              {activityName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {filteredSubActivities.length > 0 && (
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth>
                          <InputLabel>Subactividad</InputLabel>
                          <Select
                            label="Subactividad"
                            name="subActivity"
                            value={selectedSubActivity}
                            onChange={handleSelectSubActivityChange}
                          >
                            {filteredSubActivities.map((subActivity, index) => (
                              <MenuItem key={index} value={subActivity}>
                                {subActivity}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    <Grid item xs={12} sm={3}>
                      <Autocomplete
                        options={supervisors}
                        getOptionLabel={(option: any) =>
                          option?.name && option?.firstName
                            ? `${option.name} ${option.firstName}`
                            : typeof option === "string"
                              ? option
                              : ""
                        }
                        value={
                          selectedSupervisor
                            ? typeof selectedSupervisor === "string"
                              ? selectedSupervisor
                              : `${selectedSupervisor.name || ""} ${selectedSupervisor.firstName || ""}`.trim()
                            : ""
                        }
                        onChange={(e, value) => {
                          if (typeof value === "string") {
                            setSelectedSupervisor(value);
                          } else if (value) {
                            setSelectedSupervisor(value);
                          } else {
                            setSelectedSupervisor("");
                          }
                        }}
                        onInputChange={(e, newInputValue) => {
                          setSelectedSupervisor(newInputValue);
                        }}
                        freeSolo
                        renderInput={(params) => (
                          <TextField {...params} label="Supervisor" variant="outlined" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Cargo"
                        variant="outlined"
                        fullWidth
                        value={selectedSupervisor?.position || role}
                        onChange={(e) => {
                          const newRole = e.target.value;
                          setRole(newRole);
                          if (selectedSupervisor && typeof selectedSupervisor === "object") {
                            setSelectedSupervisor((prev: any) => ({
                              ...prev,
                              position: newRole,
                            }));
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Equipos"
                        variant="outlined"
                        fullWidth
                        value={team}
                        onChange={(e) => setTeam(e.target.value)}
                        multiline
                        rows={3}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Observaciones"
                        variant="outlined"
                        fullWidth
                        value={observation}
                        onChange={(e) => setObservation(e.target.value)}
                        multiline
                        rows={3}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={handleCheckboxChange}
                            name="checkedBox"
                            color="primary"
                          />
                        }
                        label="Copiar datos de la semana anterior"
                      />
                    </Grid>

                    <Grid item xs={12} md={12}>
                        {error && <Typography color="error" mb={1}>{error}</Typography>}
                        <Box display="flex" justifyContent="flex-end">
                            <Button
                            variant="contained"
                            onClick={handleAddSchedule}
                            disabled={!selectedProject || !selectedDate}
                            >
                            Agregar
                            </Button>
                        </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Grid>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#004F86", fontWeight: "bold" }}>Actividad</TableCell>
                        {["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"].map((day) => (
                          <TableCell key={day} sx={{ color: "#004F86", fontWeight: "bold" }}>{day}</TableCell>
                        ))}
                        <TableCell sx={{ color: "#004F86", fontWeight: "bold" }}>Supervisor</TableCell>
                        <TableCell sx={{ color: "#004F86", fontWeight: "bold" }}>Cargo</TableCell>
                        <TableCell sx={{ color: "#004F86", fontWeight: "bold" }}>Equipos</TableCell>
                        <TableCell sx={{ color: "#004F86", fontWeight: "bold" }}>Observaciones</TableCell>
                        <TableCell sx={{ color: "#004F86", fontWeight: "bold" }}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {schedule.map((entry, index) => (
                        <TableRow key={`${entry.activity.id}-${entry.workFront}-${index}`}>
                          <TableCell>{entry.activity.name} {entry.activity.subActivity}</TableCell>

                          {["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"].map((day, dayIndex) => {
                            const dayDate = highlightedDates[dayIndex];
                            const formattedDay = dayDate ? format(dayDate, "dd/MM/yyyy") : "";
                            const towersForDay = entry.towers.filter((tower: any) => tower.day === formattedDay);

                            return (
                              <TableCell key={`${entry.activity.id}-${day}`}>
                                {editableRow === index ? (
                                  <Autocomplete
                                    multiple
                                    options={towers}
                                    getOptionLabel={(option: any) => option.towerNumber}
                                    value={tempData[index]?.towers.filter((tower: any) => tower.day === formattedDay) || []}
                                    onChange={(e, value) => {
                                      const updatedTowers = [
                                        ...tempData[index].towers.filter((tower: any) => tower.day !== formattedDay),
                                        ...value.map((tower: any) => ({ ...tower, day: formattedDay })),
                                      ];
                                      setTempData((prev) => prev.map((row, rowIndex) => rowIndex === index ? { ...row, towers: updatedTowers } : row));
                                    }}
                                    renderInput={(params) => <TextField {...params} label={`Torres`} fullWidth />}
                                  />
                                ) : towersForDay.length > 0 ? (
                                  towersForDay.map((tower: any) => tower.towerNumber).join(", ")
                                ) : ""}
                              </TableCell>
                            );
                          })}

                          <TableCell>
                            {editableRow === index ? (
                              <Autocomplete
                                options={supervisors}
                                getOptionLabel={(option: any) => {
                                  if (typeof option === "string") return option;
                                  if (option?.name && option?.firstName) return `${option.name} ${option.firstName}`;
                                  return option?.name || "";
                                }}
                                value={
                                  typeof tempData[index]?.supervisor === "string"
                                    ? tempData[index]?.supervisor
                                    : supervisors.find(
                                      (supervisor) => supervisor.rut === tempData[index]?.supervisor?.rut
                                    ) || tempData[index]?.supervisor || null
                                }
                                onChange={(e, value) => {
                                  setTempData((prev) => prev.map((row, i) => i === index ? {
                                      ...row,
                                      supervisor: typeof value === "string" ? { name: value, position: row.role || "" } : value,
                                      role: value?.position || row.role || "",
                                    } : row
                                  ));
                                }}
                                onInputChange={(e, newInputValue) => {
                                  setTempData((prev) => prev.map((row, i) => i === index ? {
                                      ...row,
                                      supervisor: { name: newInputValue, position: row.role || "" },
                                    } : row
                                  ));
                                }}
                                freeSolo
                                renderInput={(params) => <TextField {...params} label="Supervisor" />}
                              />
                            ) : (
                              typeof tempData[index]?.supervisor === "string"
                                ? tempData[index]?.supervisor
                                : tempData[index]?.supervisor?.name || ""
                            )}
                          </TableCell>

                          <TableCell>
                            {editableRow === index ? (
                              <TextField
                                value={tempData[index]?.role || ""}
                                onChange={(e) => {
                                  const newRole = e.target.value;
                                  setTempData((prev) => prev.map((row, i) => i === index ? { ...row, role: newRole, supervisor: { ...row.supervisor, position: newRole }, } : row));
                                }}
                                label="Cargo"
                              />
                            ) : (entry.role || "")}
                          </TableCell>

                          <TableCell>
                            {editableRow === index ? (
                              <TextField
                                value={tempData[index]?.team || ""}
                                onChange={(e) => setTempData((prev) => prev.map((row, i) => i === index ? { ...row, team: e.target.value } : row))}
                              />
                            ) : (entry.team || "")}
                          </TableCell>

                          <TableCell>
                            {editableRow === index ? (
                              <TextField
                                value={tempData[index]?.observations || ""}
                                onChange={(e) => setTempData((prev) => prev.map((row, i) => i === index ? { ...row, observations: e.target.value } : row))}
                              />
                            ) : (entry.observations || "")}
                          </TableCell>

                          <TableCell>
                            {editableRow === index ? (
                              <>
                                <Save
                                  color="primary"
                                  style={{ cursor: "pointer", marginRight: 8 }}
                                  onClick={() => {
                                    const updatedData = tempData[index];
                                    if (!updatedData) { setEditableRow(null); return; }
                                    setSchedule((prev) => {
                                      const updatedSchedule = [...prev];
                                      updatedSchedule[index] = updatedData;
                                      return updatedSchedule;
                                    });
                                    setEditableRow(null);
                                  }}
                                />
                                <Cancel
                                  color="error"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => { setTempData(schedule); setEditableRow(null); }}
                                />
                              </>
                            ) : (
                              <>
                                <Edit
                                  color="primary"
                                  style={{ cursor: "pointer", marginRight: 8 }}
                                  onClick={() => { setEditableRow(index); setTempData(schedule); }}
                                />
                                <Delete
                                  color="error"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleDeleteActivity(entry.activity.id, entry.workFront)}
                                />
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <Grid display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      color="primary"
                      sx={{ marginTop: 4 }}
                      onClick={handleExportToExcel}
                      startIcon={<FileDownloadIcon />}
                    >
                      Exportar a Excel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ marginTop: 4 }}
                      onClick={handleSaveSchedule}
                      startIcon={<Save />}
                    >
                      Guardar Programación
                    </Button>
                  </Grid>
                </Grid>
            </Box>
        )}

        {/* --- PESTAÑA 1: HISTORIAL --- */}
        {tabValue === 1 && (
            <Box>
                {!selectedProject ? (
                    <Alert severity="warning">Selecciona un proyecto arriba para ver sus informes.</Alert>
                ) : historyData.length === 0 ? (
                    <Alert severity="info">No se encontraron registros previos para este proyecto.</Alert>
                ) : (
                    <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Semana (Periodo)</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Registros</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Supervisores Involucrados</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Última Actualización</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#004F86' }}>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyData.map((row, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {format(row.startDate, "dd/MM/yyyy")} - {format(row.endDate, "dd/MM/yyyy")}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Semana {moment(row.startDate).isoWeek()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={`${row.count} Actividades`} size="small" color="primary" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {row.supervisors || "Sin asignar"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {format(row.lastUpdate, "dd/MM/yyyy HH:mm")}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Ver / Editar">
                                                <IconButton color="primary" onClick={() => handleViewReport(row.startDate)}>
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Descargar Excel">
                                                <IconButton color="secondary" onClick={() => handleDownloadReport(row.startDate, row.endDate)}>
                                                    <FileDownloadIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </Box>
        )}
      </Box>
    </>
  );
};

export default WeeklyProgramming;